import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, LinearProgress,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, IconButton, TextField, MenuItem, Select, FormControl, InputLabel,
  Checkbox, TableSortLabel, Collapse, Stack, Divider, OutlinedInput, ListItemText,
  ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockIcon from '@mui/icons-material/Lock';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import {
  getCustomerInstances,
  startHealthCheckRun,
  stopHealthCheckRun,
  pauseHealthCheckRun,
  resumeHealthCheckRun,
  getHealthCheckStatus,
  getHealthCheckResults,
  getHealthCheckRuns,
  clearHealthCheckRuns,
  rerunHealthCheckFailed,
} from '../../services/apiService';

const DARK_GREEN = '#1D4434';
const POLL_INTERVAL = 3000;

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtMs = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const instId     = (row) => row._id;
const instName   = (row) => row['Instance Name']   || row.instanceName   || '';
const instDomain = (row) => row['Instance Domain'] || row.instanceDomain || '';
const instRegion = (row) => row['Region']          || row.region         || '';
const instGroup  = (row) => row['Group']           || row.group          || '';
const instAppServer = (row) => row['App server']   || row.appServer      || '';

// ─── shared sub-components ────────────────────────────────────────────────────

function StatusChip({ status }) {
  const cfgMap = {
    healthy:   { color: 'success', label: 'Healthy',   icon: <CheckCircleIcon fontSize="small" /> },
    partial:   { color: 'warning', label: 'Partial',   icon: <WarningIcon fontSize="small" /> },
    unhealthy: { color: 'error',   label: 'Unhealthy', icon: <ErrorIcon fontSize="small" /> },
    error:     { color: 'error',   label: 'Error',     icon: <ErrorIcon fontSize="small" /> },
  };
  const cfg = cfgMap[status] || { color: 'default', label: status || '—', icon: <HelpOutlineIcon fontSize="small" /> };
  return <Chip size="small" color={cfg.color} label={cfg.label} icon={cfg.icon} />;
}

function NodeCell({ node }) {
  if (!node) return <TableCell>—</TableCell>;
  return (
    <TableCell sx={{ verticalAlign: 'top', minWidth: 190 }}>
      <Stack spacing={0.4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {node.healthy
            ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 15 }} />
            : <ErrorIcon sx={{ color: 'error.main', fontSize: 15 }} />}
          <Typography variant="caption"
            sx={{ color: node.healthy ? 'success.main' : 'error.main', fontWeight: 700, fontFamily: 'monospace' }}>
            {node.machineName || (node.error ? '—' : 'No response')}
          </Typography>
        </Box>
        {node.routeId && (
          <Typography variant="caption" color="text.secondary"
            sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
            {node.routeId}
          </Typography>
        )}
        {node.latencyMs != null && (
          <Typography variant="caption" color="text.secondary">{fmtMs(node.latencyMs)}</Typography>
        )}
        {node.error && (
          <Typography variant="caption" color="error.main"
            sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
            {node.error}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}

function HazelcastCell({ size, valid }) {
  if (size === null || size === undefined) return <TableCell>—</TableCell>;
  return (
    <TableCell sx={{ verticalAlign: 'top', minWidth: 90 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {valid
          ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 15 }} />
          : <ErrorIcon sx={{ color: 'error.main', fontSize: 15 }} />}
        <Typography variant="caption"
          sx={{ fontFamily: 'monospace', fontWeight: 700,
                color: valid ? 'success.main' : 'error.main' }}>
          {size} node{size !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </TableCell>
  );
}

function AllHostsCell({ valid, error, hosts }) {
  if (valid === null || valid === undefined) return <TableCell>—</TableCell>;
  const hostList = (hosts || []).join('\n');
  return (
    <TableCell sx={{ verticalAlign: 'top', minWidth: 120 }}>
      <Tooltip title={error || hostList || ''} placement="top" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: error || hostList ? 'help' : 'default' }}>
          {valid
            ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 15 }} />
            : <ErrorIcon sx={{ color: 'error.main', fontSize: 15 }} />}
          <Typography variant="caption"
            sx={{ color: valid ? 'success.main' : 'error.main', fontWeight: 600,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
            {valid ? 'Hostnames OK' : (error || 'Invalid')}
          </Typography>
        </Box>
      </Tooltip>
    </TableCell>
  );
}

/** Compact results table used both on the main page and inside the modal. */
function ResultsTable({ results, loading, isLive, defaultStatus = 'all' }) {
  const [sortBy, setSortBy]   = useState('instanceName');
  const [sortDir, setSortDir] = useState('asc');
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [searchText, setSearchText]     = useState('');
  const [regionFilter, setRegionFilter] = useState([]);

  const regionOptions = useMemo(() =>
    [...new Set(results.map(r => r.region).filter(Boolean))].sort(),
  [results]);

  const failures = useMemo(() => results.filter(r => r.status !== 'healthy'), [results]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const visible = useMemo(() => {
    let base = statusFilter === 'issues' ? failures : results;
    if (searchText) base = base.filter(r =>
      (r.instanceName || '').toLowerCase().includes(searchText.toLowerCase()));
    if (regionFilter.length) base = base.filter(r => regionFilter.includes(r.region));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => String(a[sortBy] ?? '').localeCompare(String(b[sortBy] ?? '')) * dir);
  }, [results, failures, statusFilter, searchText, regionFilter, sortBy, sortDir]);

  return (
    <Box>
      {/* filter bar */}
      <Box sx={{ px: 0, pb: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {loading && <CircularProgress size={16} />}
          {results.length > 0 && (
            <>
              <Chip size="small" color="success" label={`✓ ${results.filter(r => r.status === 'healthy').length} healthy`} />
              <Chip size="small" color="warning" label={`⚠ ${results.filter(r => r.status === 'partial').length} partial`} />
              <Chip size="small" color="error"
                label={`✗ ${results.filter(r => ['unhealthy','error'].includes(r.status)).length} unresponsive`} />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* status toggle */}
          <ToggleButtonGroup size="small" value={statusFilter} exclusive
            onChange={(_, v) => { if (v) setStatusFilter(v); }}>
            <ToggleButton value="all">All ({results.length})</ToggleButton>
            <ToggleButton value="issues"
              sx={{ color: failures.length > 0 ? 'error.main' : undefined }}>
              Issues ({failures.length})
            </ToggleButton>
          </ToggleButtonGroup>
          {/* region filter */}
          {regionOptions.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Region</InputLabel>
              <Select multiple value={regionFilter}
                onChange={e => setRegionFilter(e.target.value)}
                input={<OutlinedInput label="Region" />}
                renderValue={v => v.join(', ')}>
                {regionOptions.map(r => (
                  <MenuItem key={r} value={r}>
                    <Checkbox checked={regionFilter.includes(r)} size="small" />
                    <ListItemText primary={r} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* name search */}
          <TextField size="small" label="Search instance" value={searchText}
            onChange={e => setSearchText(e.target.value)} sx={{ width: 180 }} />
          {(searchText || regionFilter.length > 0) && (
            <Button size="small" onClick={() => { setSearchText(''); setRegionFilter([]); }}>Clear</Button>
          )}
        </Box>
      </Box>

      {/* table */}
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>
                <TableSortLabel active={sortBy === 'instanceName'}
                  direction={sortBy === 'instanceName' ? sortDir : 'asc'}
                  onClick={() => handleSort('instanceName')}>Instance</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'region'}
                  direction={sortBy === 'region' ? sortDir : 'asc'}
                  onClick={() => handleSort('region')}>Region</TableSortLabel>
              </TableCell>
              <TableCell>Server Prefix</TableCell>
              <TableCell>Initial Machine</TableCell>
              <TableCell>Node A</TableCell>
              <TableCell>Node B</TableCell>
              <TableCell>Hazelcast</TableCell>
              <TableCell>All Hosts</TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortDir : 'asc'}
                  onClick={() => handleSort('status')}>Status</TableSortLabel>
              </TableCell>
              <TableCell>Checked At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading
                      ? 'Loading…'
                      : isLive
                        ? 'Results will appear as checks complete…'
                        : 'No results match filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : visible.map(r => (
              <TableRow key={r._id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{r.instanceName || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {r.instanceDomain || ''}
                  </Typography>
                  {r.redirectedTo && (
                    <Tooltip title={`Redirected → ${r.redirectedTo} (domain updated in DB)`} placement="right" arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
                        <OpenInNewIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                        <Typography variant="caption"
                          sx={{ color: 'warning.main', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.65rem' }}>
                          {r.redirectedTo}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell><Typography variant="body2">{r.region || '—'}</Typography></TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.serverPrefix || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.initialMachineName || '—'}</Typography>
                </TableCell>
                <NodeCell node={r.nodeA} />
                <NodeCell node={r.nodeB} />
                <HazelcastCell size={r.hazelcastSize} valid={r.hazelcastValid} />
                <AllHostsCell valid={r.allHostsValid} error={r.allHostsError} hosts={r.allHostsHosts} />
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{fmtDate(r.checkedAt)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Modal that opens when clicking View in the history table. */
function RunDetailModal({ run, onClose }) {
  const [results, setResults]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunDone, setRerunDone]     = useState(false);
  const [rerunProgress, setRerunProgress] = useState({ completed: 0, total: 0, pct: 0 });
  const [rerunError, setRerunError]   = useState(null);
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (!run) return;
    setLoading(true); setError(null); setIsRerunning(false); setRerunDone(false); setRerunError(null);
    getHealthCheckResults(run._id)
      .then(res => setResults(res.results || []))
      .catch(err => setError(err.message || 'Failed to load results'))
      .finally(() => setLoading(false));
    return stopPoll;
  }, [run?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRerun = async () => {
    setRerunError(null);
    try {
      const res = await rerunHealthCheckFailed(run._id);
      if (!res.success) throw new Error(res.message);
      setIsRerunning(true);
      setRerunDone(false);
      setRerunProgress({ completed: 0, total: res.run?.total || 0, pct: 0 });

      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const [resultsRes, statusRes] = await Promise.all([
            getHealthCheckResults(run._id),
            getHealthCheckStatus(),
          ]);
          setResults(resultsRes.results || []);

          const stillActive = statusRes.active && String(statusRes.run?._id) === String(run._id);
          if (stillActive) {
            setRerunProgress({
              completed: statusRes.run.completed || 0,
              total:     statusRes.run.total     || 0,
              pct:       statusRes.progressPct   || 0,
            });
          } else {
            stopPoll();
            setIsRerunning(false);
            setRerunDone(true);
          }
        } catch { /* ignore transient poll errors */ }
      }, 3000);
    } catch (err) {
      setRerunError(err.message || 'Failed to start re-run');
    }
  };

  if (!run) return null;

  const failureCount = results.filter(r => r.status !== 'healthy').length;

  return (
    <Dialog open={!!run} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color={DARK_GREEN}>
            Run Details
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtDate(run.createdAt)}
            {run.startedBy && ` · by ${run.startedBy}`}
            {' · '}
            <Chip size="small" label={run.trigger || 'manual'} sx={{ height: 18, fontSize: '0.7rem' }} />
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
        {/* run summary strip */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`${run.total} total`} />
          <Chip color="success" label={`✓ ${results.filter(r => r.status === 'healthy').length} healthy`} />
          <Chip color="warning" label={`⚠ ${results.filter(r => r.status === 'partial').length} partial`} />
          <Chip color="error"   label={`✗ ${results.filter(r => ['unhealthy','error'].includes(r.status)).length} unhealthy`} />
        </Stack>

        {/* re-run progress */}
        {isRerunning && (
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'primary.light', bgcolor: '#e8f4fd', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" fontWeight={600}>
                Re-running failed instances — {rerunProgress.completed}/{rerunProgress.total} ({rerunProgress.pct}%) · results updating live
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={rerunProgress.pct}
              sx={{ height: 5, borderRadius: 2 }} />
          </Paper>
        )}
        {rerunDone && (
          <Alert severity="success" sx={{ mb: 2 }}>Re-run complete — results updated</Alert>
        )}

        {error      && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {rerunError && <Alert severity="error" sx={{ mb: 2 }}>{rerunError}</Alert>}

        <ResultsTable results={results} loading={loading} isLive={isRerunning} defaultStatus="issues" />
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, gap: 1 }}>
        {!isRerunning && failureCount > 0 && (
          <Button variant="contained" startIcon={<PlayArrowIcon />}
            sx={{ bgcolor: DARK_GREEN, '&:hover': { bgcolor: '#163628' } }}
            onClick={handleRerun}>
            Re-run Failed ({failureCount})
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function HealthCheckTab() {
  const [rows, setRows]                   = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(true);

  const [filterRegions, setFilterRegions] = useState([]);
  const [filterGroups, setFilterGroups]   = useState([]);
  const [filterAppServer, setFilterAppServer] = useState('');
  const [filterSearch, setFilterSearch]   = useState('');
  const [showFilters, setShowFilters]     = useState(true);

  const [selectedIds, setSelectedIds]     = useState(new Set());

  const [currentRun, setCurrentRun]       = useState(null);
  const [progressPct, setProgressPct]     = useState(0);
  const [runLoading, setRunLoading]       = useState(false);
  const [runError, setRunError]           = useState(null);

  const [pastRuns, setPastRuns]           = useState([]);
  const [confirmOpen, setConfirmOpen]     = useState(false);

  // modal state
  const [modalRun, setModalRun]           = useState(null);

  const pollRef = useRef(null);

  // ── load instances ───────────────────────────────────────────────────────

  const loadInstances = useCallback(async () => {
    setInstancesLoading(true);
    try {
      const data = await getCustomerInstances();
      setRows(data.rows || []);
    } catch (err) {
      console.error('[HealthCheck] Failed to load instances:', err);
    } finally {
      setInstancesLoading(false);
    }
  }, []);

  // ── polling ──────────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getHealthCheckStatus();
      setCurrentRun(res.run || null);
      setProgressPct(res.progressPct || 0);
    } catch (err) {
      console.error('[HealthCheck] Status poll error:', err);
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => fetchStatus(), POLL_INTERVAL);
  }, [fetchStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => {
    loadInstances();
    fetchStatus();
    startPolling();
    getHealthCheckRuns(20).then(r => setPastRuns(r.runs || [])).catch(() => {});
    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentRun) return;
    if (['completed', 'stopped'].includes(currentRun.status)) {
      stopPolling();
      getHealthCheckRuns(20).then(r => setPastRuns(r.runs || [])).catch(() => {});
    } else {
      startPolling();
    }
  }, [currentRun?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── filter options ───────────────────────────────────────────────────────

  const regionOptions    = useMemo(() => [...new Set(rows.map(r => instRegion(r)).filter(Boolean))].sort(), [rows]);
  const groupOptions     = useMemo(() => [...new Set(rows.map(r => instGroup(r)).filter(Boolean))].sort(), [rows]);
  const appServerOptions = useMemo(() => [...new Set(rows.map(r => instAppServer(r)).filter(Boolean))].sort(), [rows]);

  const filteredRows = useMemo(() => rows.filter(row => {
    if (filterRegions.length && !filterRegions.includes(instRegion(row))) return false;
    if (filterGroups.length  && !filterGroups.includes(instGroup(row)))   return false;
    if (filterAppServer && !instAppServer(row).toLowerCase().includes(filterAppServer.toLowerCase())) return false;
    if (filterSearch    && !instName(row).toLowerCase().includes(filterSearch.toLowerCase())) return false;
    return true;
  }), [rows, filterRegions, filterGroups, filterAppServer, filterSearch]);

  // ── selection ────────────────────────────────────────────────────────────

  const allFilteredSelected = filteredRows.length > 0 && filteredRows.every(r => selectedIds.has(instId(r)));
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => { const n = new Set(prev); filteredRows.forEach(r => n.delete(instId(r))); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); filteredRows.forEach(r => n.add(instId(r))); return n; });
    }
  };
  const toggleOne = (id) => setSelectedIds(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  // ── run controls ─────────────────────────────────────────────────────────

  const isActive = currentRun && ['running', 'paused', 'stopping'].includes(currentRun.status);

  const buildFilter = () => {
    if (someSelected) return { instanceIds: [...selectedIds] };
    const hasFilters = filterRegions.length || filterGroups.length || filterAppServer;
    if (!hasFilters) return null;
    return {
      regions: filterRegions.length ? filterRegions : undefined,
      groups:  filterGroups.length  ? filterGroups  : undefined,
    };
  };

  const handleStart = async () => {
    setConfirmOpen(false);
    setRunError(null); setRunLoading(true);
    try {
      const res = await startHealthCheckRun(buildFilter());
      if (!res.success) throw new Error(res.message);
      setCurrentRun(res.run);
      startPolling();
    } catch (err) {
      setRunError(err.message || String(err));
    } finally { setRunLoading(false); }
  };

  const handleStop   = async () => { try { await stopHealthCheckRun();   await fetchStatus(); } catch (e) { setRunError(e.message); } };
  const handlePause  = async () => { try { await pauseHealthCheckRun();  await fetchStatus(); } catch (e) { setRunError(e.message); } };
  const handleResume = async () => { try { await resumeHealthCheckRun(); await fetchStatus(); } catch (e) { setRunError(e.message); } };

  const handleClearHistory = async () => {
    try {
      await clearHealthCheckRuns();
      setPastRuns([]);
    } catch (e) { setRunError(e.message); }
  };

  const runLabel = () => {
    if (someSelected) return `Run selected (${selectedIds.size})`;
    if (filteredRows.length < rows.length) return `Run filtered (${filteredRows.length})`;
    return 'Run all';
  };

  // ─────────────────────────────────────────────────────────────────────────

  // ── summary cards data ────────────────────────────────────────────────────
  const lastRun   = pastRuns[0] || null;
  const refRun    = isActive ? currentRun : lastRun;   // show live counts while running
  const totalRuns = pastRuns.length + (isActive ? 1 : 0);
  const healthPct = refRun && refRun.total > 0
    ? Math.round((refRun.healthy / refRun.total) * 100)
    : null;

  const summaryCards = [
    {
      label: 'Total Runs',
      value: totalRuns || '—',
      sub: lastRun ? `Last: ${fmtDate(lastRun.createdAt)}` : 'No runs yet',
      color: DARK_GREEN,
    },
    {
      label: 'Instances Checked',
      value: refRun ? refRun.total : '—',
      sub: refRun ? (isActive ? 'In progress' : fmtDate(refRun.createdAt)) : 'Last run',
      color: '#1565c0',
    },
    {
      label: 'Healthy',
      value: refRun ? refRun.healthy : '—',
      sub: healthPct != null ? `${healthPct}% of total` : '',
      color: '#2e7d32',
    },
    {
      label: 'Partial',
      value: refRun ? (refRun.partial || 0) : '—',
      sub: 'One node responding',
      color: '#e65100',
    },
    {
      label: 'Unhealthy / Errors',
      value: refRun ? ((refRun.unhealthy || 0) + (refRun.errors || 0)) : '—',
      sub: 'Needs attention',
      color: '#c62828',
    },
  ];

  return (
    <Box>
      {/* ── Summary cards ─────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 2 }}>
        {summaryCards.map(card => (
          <Paper key={card.label} elevation={1} sx={{ p: 2, borderTop: `3px solid ${card.color}`, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {card.label}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: card.color, my: 0.5 }}>
              {card.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ── Schedule banner ───────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScheduleIcon sx={{ color: DARK_GREEN }} />
          <Box>
            <Typography variant="body2" fontWeight={600} color={DARK_GREEN}>
              Automatic schedule: Every weekday (Mon–Fri) at 05:00 AM IST
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hits getCBV.action twice per instance — hit 1 confirms the responding node via
              Set-Cookie ROUTEID; hit 2 uses the amended ROUTEID to verify the other node.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── Active run banner ─────────────────────────────────── */}
      {isActive && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: '#e8f4fd' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" fontWeight={600}>
              {currentRun.status === 'paused' ? 'Paused' : 'Running'} —&nbsp;
              {currentRun.completed}/{currentRun.total} instances ({progressPct}%)
              {currentRun.startedBy && ` · by ${currentRun.startedBy}`}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressPct} sx={{ height: 7, borderRadius: 3, mb: 1.5 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            {currentRun.status === 'running' && (
              <Button size="small" variant="outlined" startIcon={<PauseIcon />} onClick={handlePause}>Pause</Button>
            )}
            {currentRun.status === 'paused' && (
              <Button size="small" variant="contained" startIcon={<PlayCircleIcon />} onClick={handleResume}>Resume</Button>
            )}
            <Button size="small" variant="outlined" color="error" startIcon={<StopIcon />} onClick={handleStop}>Stop</Button>
            <Chip size="small" color="success" variant="outlined" label={`✓ ${currentRun.healthy} healthy`} />
            <Chip size="small" color="warning" variant="outlined" label={`⚠ ${currentRun.partial || 0} partial`} />
            <Chip size="small" color="error"   variant="outlined" label={`✗ ${currentRun.unhealthy} unhealthy`} />
            {currentRun.errors > 0 && <Chip size="small" color="error" label={`! ${currentRun.errors} errors`} />}
          </Stack>
        </Paper>
      )}

      {runError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRunError(null)}>{runError}</Alert>}


      {/* ── Filters + Master List ─────────────────────────────── */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: DARK_GREEN }} />
            <Typography variant="subtitle1" fontWeight={600} color={DARK_GREEN}>Instance List</Typography>
            {!instancesLoading && <Chip size="small" label={`${filteredRows.length} / ${rows.length}`} />}
            {someSelected && (
              <Chip size="small" color="primary" label={`${selectedIds.size} selected`}
                onDelete={() => setSelectedIds(new Set())} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <IconButton size="small" onClick={loadInstances} disabled={instancesLoading} title="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setShowFilters(f => !f)}>
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Region</InputLabel>
              <Select multiple value={filterRegions} onChange={e => setFilterRegions(e.target.value)}
                input={<OutlinedInput label="Region" />} renderValue={v => v.join(', ')}>
                {regionOptions.map(r => (
                  <MenuItem key={r} value={r}>
                    <Checkbox checked={filterRegions.includes(r)} size="small" />
                    <ListItemText primary={r} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Group</InputLabel>
              <Select multiple value={filterGroups} onChange={e => setFilterGroups(e.target.value)}
                input={<OutlinedInput label="Group" />} renderValue={v => v.join(', ')}>
                {groupOptions.map(g => (
                  <MenuItem key={g} value={g}>
                    <Checkbox checked={filterGroups.includes(g)} size="small" />
                    <ListItemText primary={g} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>App Server</InputLabel>
              <Select value={filterAppServer} onChange={e => setFilterAppServer(e.target.value)} label="App Server">
                <MenuItem value="">All</MenuItem>
                {appServerOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField size="small" label="Search instance name" value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)} sx={{ minWidth: 180 }} />

            <Button size="small" onClick={() => {
              setFilterRegions([]); setFilterGroups([]); setFilterAppServer(''); setFilterSearch('');
            }}>Clear filters</Button>
          </Box>
        </Collapse>

        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          {isActive ? (
            <Tooltip title={`Run in progress — started by ${currentRun.startedBy || 'unknown'}`}>
              <span>
                <Button variant="contained" disabled startIcon={<LockIcon />}>
                  {currentRun.status === 'paused' ? 'Paused' : 'Running…'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button variant="contained"
              sx={{ bgcolor: DARK_GREEN, '&:hover': { bgcolor: '#163628' } }}
              startIcon={runLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              disabled={runLoading || instancesLoading || rows.length === 0}
              onClick={() => setConfirmOpen(true)}>
              {runLabel()}
            </Button>
          )}
        </Box>

        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" indeterminate={someSelected && !allFilteredSelected}
                    checked={allFilteredSelected} onChange={toggleAll} disabled={isActive} />
                </TableCell>
                <TableCell>Instance</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>App Server</TableCell>
                <TableCell>Domain</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instancesLoading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {rows.length === 0 ? 'No instances loaded' : 'No instances match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRows.map(row => {
                const id = instId(row);
                return (
                  <TableRow key={id} hover selected={selectedIds.has(id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedIds.has(id)}
                        onChange={() => toggleOne(id)} disabled={isActive} />
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={500}>{instName(row)}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{instRegion(row) || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{instGroup(row) || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{instAppServer(row) || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{instDomain(row) || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Run History ───────────────────────────────────────── */}
      <Paper elevation={1}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600} color={DARK_GREEN}>
            Run History ({pastRuns.length})
          </Typography>
          {pastRuns.length > 0 && (
            <Button size="small" color="error" variant="outlined" onClick={handleClearHistory}>
              Clear History
            </Button>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                <TableCell>Started</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Healthy</TableCell>
                <TableCell>Partial</TableCell>
                <TableCell>Unhealthy</TableCell>
                <TableCell>Errors</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Active run pinned at top */}
              {isActive && currentRun && (
                <TableRow sx={{ bgcolor: '#e8f4fd' }}>
                  <TableCell><Typography variant="caption">{fmtDate(currentRun.createdAt)}</Typography></TableCell>
                  <TableCell><Chip size="small" label={currentRun.trigger || 'manual'} /></TableCell>
                  <TableCell><Typography variant="caption">{currentRun.startedBy || '—'}</Typography></TableCell>
                  <TableCell>{currentRun.total}</TableCell>
                  <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{currentRun.healthy}</TableCell>
                  <TableCell sx={{ color: 'warning.main', fontWeight: 600 }}>{currentRun.partial || 0}</TableCell>
                  <TableCell sx={{ color: 'error.main',   fontWeight: 600 }}>{currentRun.unhealthy}</TableCell>
                  <TableCell>{currentRun.errors}</TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        {currentRun.status === 'paused' ? 'Paused' : 'Running'} {progressPct}%
                      </Typography>
                      <LinearProgress variant="determinate" value={progressPct}
                        sx={{ height: 5, borderRadius: 2, mt: 0.5 }} />
                    </Box>
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => setModalRun(currentRun)}>View</Button>
                  </TableCell>
                </TableRow>
              )}
              {/* Completed runs */}
              {!isActive && pastRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">No historical runs yet</Typography>
                  </TableCell>
                </TableRow>
              ) : pastRuns.map(run => (
                <TableRow key={run._id} hover>
                  <TableCell><Typography variant="caption">{fmtDate(run.createdAt)}</Typography></TableCell>
                  <TableCell><Chip size="small" label={run.trigger || 'manual'} /></TableCell>
                  <TableCell><Typography variant="caption">{run.startedBy || '—'}</Typography></TableCell>
                  <TableCell>{run.total}</TableCell>
                  <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{run.healthy}</TableCell>
                  <TableCell sx={{ color: 'warning.main', fontWeight: 600 }}>{run.partial || 0}</TableCell>
                  <TableCell sx={{ color: 'error.main',   fontWeight: 600 }}>{run.unhealthy}</TableCell>
                  <TableCell>{run.errors}</TableCell>
                  <TableCell>
                    <Chip size="small" label={run.status}
                      color={run.status === 'completed' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell><Typography variant="caption">{fmtDate(run.completedAt)}</Typography></TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => setModalRun(run)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Run confirmation dialog ───────────────────────────── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start Health Check?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {someSelected
              ? `Run health check on ${selectedIds.size} selected instance(s)?`
              : filteredRows.length < rows.length
                ? `Run health check on ${filteredRows.length} filtered instance(s)?`
                : `Run health check on all ${rows.length} instances?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStart}
            sx={{ bgcolor: DARK_GREEN, '&:hover': { bgcolor: '#163628' } }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Run Detail Modal ──────────────────────────────────── */}
      <RunDetailModal run={modalRun} onClose={() => setModalRun(null)} />
    </Box>
  );
}
